import { useMemo } from 'react';

// Components
import { IconScale } from '@u21/tabler-icons';
import {
  U21Chip,
  U21ChipProps,
  U21Spacer,
  U21Subsection,
  U21Typography,
} from 'app/shared/u21-ui/components';
import styled from 'styled-components';

// Models
import {
  EvaluationLog,
  EvaluationLogStatus,
  ShadowEvaluationLog,
} from 'app/shared/eventsOld/models';

// Utils
import { RuleIcon } from 'app/modules/detectionModels/components/RuleIcon';
import { RuleStatuses } from 'app/modules/rules/models';
import { RuleTooltip } from 'app/modules/detectionModels/components/RuleTooltip';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { SidebarFlowTriggeredInfo } from 'app/modules/sidebar/components/SidebarFlowTriggeredInfo';
import { startCase } from 'lodash';

interface Props {
  evaluationLog: EvaluationLog;
  shadowEvaluationLog: ShadowEvaluationLog;
}

const STATUS_COLOR: Record<EvaluationLogStatus, U21ChipProps['color']> = {
  PASS: 'success',
  FAIL: 'error',
  ERROR: 'default', // system error color, need dev to investigate
};

export const SidebarEvaluationLogInfo = ({
  evaluationLog,
  shadowEvaluationLog,
}: Props) => {
  const ruleEvaluations = useMemo(() => {
    const activeRules = Object.keys(evaluationLog.rule_executions).map(
      (ruleId) => {
        const ruleEvaluation = evaluationLog.rule_executions[ruleId];
        return {
          id: ruleId,
          name: ruleEvaluation.rule_name,
          status: ruleEvaluation.status,
          rule_status: 'ACTIVE',
        };
      },
    );
    const shadowRules = Object.keys(shadowEvaluationLog.rule_executions)
      .filter(
        (ruleId) =>
          shadowEvaluationLog.rule_executions[ruleId].rule_status ===
          'ACTIVE_SHADOW',
      )
      .map((ruleId) => {
        const ruleEvaluation = shadowEvaluationLog.rule_executions[ruleId];
        return {
          id: ruleId,
          name: ruleEvaluation.rule_name,
          status: ruleEvaluation.status,
          rule_status: 'ACTIVE_SHADOW',
        };
      });
    return [...activeRules, ...shadowRules];
  }, [evaluationLog.rule_executions, shadowEvaluationLog.rule_executions]);

  const failed = useMemo(
    () => ruleEvaluations.filter((e) => e.status !== 'PASS').length,
    [ruleEvaluations],
  );

  if (!evaluationLog.evaluation_id) return null;

  return (
    <>
      <U21Subsection
        collapsed
        collapsible
        shadedHeader
        title={
          <U21Spacer horizontal>
            <span>Rules triggered</span>
            <U21Chip>{ruleEvaluations.length}</U21Chip>
            <NonTransformedTypography variant="body2">
              ({failed} out of {ruleEvaluations.length} failed)
            </NonTransformedTypography>
          </U21Spacer>
        }
        titleIcon={<IconScale />}
      >
        <U21Spacer spacing={1}>
          {ruleEvaluations.map((ruleEvaluation) => {
            return (
              <SpaceBetweenDiv key={ruleEvaluation.id}>
                <EllipsisChip
                  variant="filled"
                  color="default"
                  icon={
                    <RuleIcon
                      status={ruleEvaluation.rule_status as RuleStatuses}
                    />
                  }
                  to={ROUTES_MAP.rulesId.path.replace(
                    ':id',
                    String(ruleEvaluation.id),
                  )}
                  tooltip={
                    <RuleTooltip
                      title={ruleEvaluation.name}
                      id={ruleEvaluation.id}
                      status={ruleEvaluation.rule_status as RuleStatuses}
                    />
                  }
                >
                  {ruleEvaluation.name}
                </EllipsisChip>
                <PaddedChip
                  disabled={ruleEvaluation.status === 'ERROR'}
                  color={STATUS_COLOR[ruleEvaluation.status]}
                >
                  {startCase(ruleEvaluation.status.toLowerCase())}
                </PaddedChip>
              </SpaceBetweenDiv>
            );
          })}
        </U21Spacer>
      </U21Subsection>
      {evaluationLog.flow && (
        <SidebarFlowTriggeredInfo flow={evaluationLog.flow} />
      )}
    </>
  );
};

const NonTransformedTypography = styled(U21Typography)`
  text-transform: none;
`;

const PaddedChip = styled(U21Chip)`
  margin-left: 10px;
`;

const EllipsisChip = styled(U21Chip)`
  overflow: hidden;
`;

const SpaceBetweenDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
